/**
 * PR-03 — Unified SkillPlatform registry.
 *
 * Aggregates the legacy skill/agent/command/MCP registries into a single
 * canonical `SkillDefinition` list without mutating the legacy sources.
 */
import { AGENT_REGISTRY, } from "../agent/registry.js";
import { COMMAND_REGISTRY, } from "../commands/registry.js";
import { MCP_CONNECTION_REGISTRY, } from "../mcp/registry.js";
import { SKILL_REGISTRY as LEGACY_SKILL_REGISTRY, } from "../skills/registry.js";
import { SKILL_REGISTRY as PROJECT_SKILL_REGISTRY } from "../project-skill-registry/skillRegistry.js";
export const SKILL_PLATFORM_REGISTRY_VERSION = "pr-03-skill-platform.v1";
function inferMode(value) {
    if (value.executionMode)
        return value.executionMode;
    if ("enabled" in value && value.enabled === false)
        return "disabled";
    if (value.realExecutionEnabled === true || value.defaultMode === "approval_required" || value.defaultMode === "live")
        return "approval_required";
    if (value.dryRunOnly === true || value.previewOnly === true || value.defaultMode === "propose_only" || value.defaultMode === "plan_only")
        return "plan_only";
    if (value.defaultMode === "read_only")
        return "read_only";
    return "disabled";
}
function inferRisk(value) {
    const r = value.risk;
    if (r === "low" || r === "medium" || r === "high" || r === "critical")
        return r;
    return "medium";
}
function normalizeProjectSkill(metadata) {
    return {
        id: metadata.skillId,
        kind: "skill",
        version: metadata.version,
        title: metadata.title,
        description: metadata.description,
        source: "project-skill-registry",
        sourceId: metadata.skillId,
        risk: inferRisk(metadata),
        mode: inferMode(metadata),
        approvalRequired: metadata.approvalRequired,
        auditEnabled: true,
        networkAllowed: false,
        productionDbAllowed: false,
        fileMutationAllowed: false,
        commandExecutionAllowed: false,
        allowedTools: [...metadata.allowedTools],
        deniedTools: [...metadata.deniedTools],
        dependsOn: [],
        capabilities: [],
        instructionsPath: metadata.instructionsPath,
        inputSchema: metadata.inputSchema,
        outputSchema: metadata.outputSchema,
        maxCost: metadata.maxCost,
        maxDurationMs: metadata.maxDuration,
        evidenceRequired: metadata.evidenceRequired,
        rollbackRequired: metadata.rollbackRequired,
        raw: metadata,
    };
}
function normalizeLegacySkill(metadata) {
    return {
        id: metadata.id,
        kind: "skill",
        version: metadata.version,
        title: metadata.name,
        description: metadata.description,
        source: "skills",
        sourceId: metadata.id,
        category: metadata.category,
        risk: inferRisk({ risk: "medium" }),
        mode: inferMode(metadata),
        approvalRequired: metadata.requireOwnerApproval,
        auditEnabled: metadata.auditEnabled,
        networkAllowed: metadata.networkAllowed,
        productionDbAllowed: metadata.productionDbAllowed,
        fileMutationAllowed: false,
        commandExecutionAllowed: false,
        allowedTools: [],
        deniedTools: [],
        dependsOn: [...metadata.relatedAgentIds],
        capabilities: [],
        maxCost: 0,
        maxDurationMs: 30000,
        evidenceRequired: true,
        rollbackRequired: false,
        raw: metadata,
    };
}
function normalizeAgent(metadata) {
    return {
        id: metadata.id,
        kind: "agent",
        version: metadata.version,
        title: metadata.name,
        description: metadata.description,
        source: "agent",
        sourceId: metadata.id,
        category: metadata.role,
        risk: inferRisk({ risk: "high" }),
        mode: metadata.executionMode ?? inferMode(metadata),
        approvalRequired: metadata.approvalRequired,
        auditEnabled: metadata.auditEnabled,
        networkAllowed: metadata.networkAllowed,
        productionDbAllowed: metadata.productionDbAllowed,
        fileMutationAllowed: metadata.fileMutationAllowed,
        commandExecutionAllowed: metadata.commandExecutionAllowed,
        allowedTools: [],
        deniedTools: [],
        dependsOn: metadata.dependsOn ? [...metadata.dependsOn] : [],
        capabilities: [...metadata.capabilities],
        maxCost: metadata.maxCost,
        maxDurationMs: metadata.maxDurationMs,
        evidenceRequired: true,
        rollbackRequired: false,
        raw: metadata,
    };
}
function normalizeCommand(metadata) {
    return {
        id: metadata.id,
        kind: "command",
        version: metadata.version,
        title: metadata.name,
        description: metadata.description,
        source: "commands",
        sourceId: metadata.id,
        category: metadata.category,
        risk: inferRisk({ risk: "medium" }),
        mode: inferMode(metadata),
        approvalRequired: metadata.requireOwnerApproval,
        auditEnabled: metadata.auditEnabled,
        networkAllowed: metadata.networkAllowed,
        productionDbAllowed: metadata.productionDbAllowed,
        fileMutationAllowed: false,
        commandExecutionAllowed: false,
        allowedTools: [],
        deniedTools: [],
        dependsOn: [...metadata.relatedAgentIds, ...metadata.relatedSkillIds],
        capabilities: [],
        maxCost: 0,
        maxDurationMs: 30000,
        evidenceRequired: true,
        rollbackRequired: false,
        raw: metadata,
    };
}
function normalizeMcp(metadata) {
    return {
        id: metadata.id,
        kind: "mcp",
        version: metadata.version,
        title: metadata.name,
        description: metadata.description,
        source: "mcp",
        sourceId: metadata.id,
        category: metadata.type,
        risk: inferRisk({ risk: "critical" }),
        mode: inferMode(metadata),
        approvalRequired: metadata.requireOwnerApproval,
        auditEnabled: metadata.auditEnabled,
        networkAllowed: metadata.networkAllowed,
        productionDbAllowed: metadata.productionDbAllowed,
        fileMutationAllowed: metadata.fileMutationAllowed,
        commandExecutionAllowed: false,
        allowedTools: [],
        deniedTools: [],
        dependsOn: [],
        capabilities: [],
        maxCost: 0,
        maxDurationMs: 30000,
        evidenceRequired: true,
        rollbackRequired: false,
        raw: metadata,
    };
}
/** Build a unified registry from all legacy sources. */
export function createSkillPlatformRegistry() {
    const now = new Date().toISOString();
    const entries = [
        ...PROJECT_SKILL_REGISTRY.map(normalizeProjectSkill),
        ...LEGACY_SKILL_REGISTRY.map(normalizeLegacySkill),
        ...AGENT_REGISTRY.map(normalizeAgent),
        ...COMMAND_REGISTRY.map(normalizeCommand),
        ...MCP_CONNECTION_REGISTRY.map(normalizeMcp),
    ];
    // De-duplicate by id, preferring the first (project-skill-registry wins).
    const seen = new Set();
    const unique = entries.filter((e) => {
        if (seen.has(e.id))
            return false;
        seen.add(e.id);
        return true;
    });
    return {
        version: SKILL_PLATFORM_REGISTRY_VERSION,
        checkedAt: now,
        entries: Object.freeze(unique),
    };
}
export const DEFAULT_SKILL_PLATFORM_REGISTRY = createSkillPlatformRegistry();
//# sourceMappingURL=registry.js.map